import React, { useState, useEffect, useMemo } from "react";
import "./PbjReport.css";

const RAILWAY = process.env.REACT_APP_API_BASE || "";

// [rowKey, column header]
const COLUMNS = [
  ["primePayId",   "PrimePayID"],
  ["firstName",    "First Name"],
  ["lastName",     "Last Name"],
  ["hireDate",     "Hire Date"],
  ["terminationDate", "Termination Date"],
  ["jtc",          "JTC"],
  ["clockInDate",  "Clock In Date"],
  ["clockInTime",  "Clock In Time"],
  ["clockOutDate", "Clock Out Date"],
  ["clockOutTime", "Clock Out Time"],
  ["hoursWorked",  "Hours Worked"],
  ["hoursWithBreak", "Hours w Break *"],
];

function csvCell(v) {
  const s = v == null ? "" : String(v);
  return /[",\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
}

export default function PbjReport() {
  const [facilities, setFacilities] = useState([]);
  const [loadingFac, setLoadingFac] = useState(true);
  const [selected, setSelected]     = useState({}); // { [id]: true }
  const [facFilter, setFacFilter]   = useState("");
  const [start, setStart]           = useState("");
  const [end, setEnd]               = useState("");
  const [rows, setRows]             = useState(null);
  const [meta, setMeta]             = useState(null);
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState(null);

  useEffect(() => {
    fetch(`${RAILWAY}/api/reports/facilities`)
      .then(r => r.json())
      .then(d => setFacilities(d.facilities || []))
      .catch(e => setError(e.message))
      .finally(() => setLoadingFac(false));
  }, []);

  const shownFacilities = useMemo(() => {
    const q = facFilter.trim().toLowerCase();
    return q ? facilities.filter(f => f.name.toLowerCase().includes(q)) : facilities;
  }, [facilities, facFilter]);

  const selectedIds = Object.keys(selected).filter(id => selected[id]);
  const canRun = start && end && selectedIds.length > 0 && !loading;

  const toggle = (id) => setSelected(s => ({ ...s, [id]: !s[id] }));
  const selectAll = () => setSelected(Object.fromEntries(shownFacilities.map(f => [f.id, true])));
  const clearAll  = () => setSelected({});

  const run = () => {
    setLoading(true); setError(null); setRows(null); setMeta(null);
    const qs = new URLSearchParams({ start, end, locations: selectedIds.join(",") });
    fetch(`${RAILWAY}/api/reports/pbj?${qs}`)
      .then(r => r.json())
      .then(d => { if (d.error) throw new Error(d.error); setRows(d.rows || []); setMeta({ start: d.start, end: d.end, count: d.count }); })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  };

  const downloadCsv = () => {
    if (!rows) return;
    const header = COLUMNS.map(c => c[1]).join(",");
    const body = rows.map(r => COLUMNS.map(c => csvCell(r[c[0]])).join(",")).join("\r\n");
    const csv = "﻿" + header + "\r\n" + body; // BOM for Excel
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `PBJ_${start}_to_${end}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="pbj">
      <div className="pbj-controls">
        <h2 className="pbj-title">PBJ Report</h2>
        <p className="pbj-sub">Payroll-Based Journal export for facility clients. Choose a date range and one or more sites, then generate.</p>

        <div className="pbj-row">
          <label className="pbj-field">
            <span>Start date</span>
            <input type="date" value={start} onChange={e => setStart(e.target.value)} />
          </label>
          <label className="pbj-field">
            <span>End date</span>
            <input type="date" value={end} onChange={e => setEnd(e.target.value)} />
          </label>
        </div>

        <div className="pbj-facilities">
          <div className="pbj-fac-head">
            <span className="pbj-fac-title">Facility sites <span className="pbj-count">({selectedIds.length} selected)</span></span>
            <div className="pbj-fac-actions">
              <input className="pbj-fac-filter" type="text" placeholder="Filter…" value={facFilter} onChange={e => setFacFilter(e.target.value)} />
              <button className="pbj-link" onClick={selectAll}>Select all</button>
              <button className="pbj-link" onClick={clearAll}>Clear</button>
            </div>
          </div>
          <div className="pbj-fac-list">
            {loadingFac && <div className="pbj-muted"><span className="spinner" /> Loading sites…</div>}
            {!loadingFac && shownFacilities.length === 0 && <div className="pbj-muted">No facility clients found.</div>}
            {shownFacilities.map(f => (
              <label key={f.id} className={`pbj-fac ${selected[f.id] ? "on" : ""}`}>
                <input type="checkbox" checked={!!selected[f.id]} onChange={() => toggle(f.id)} />
                <span>{f.name}</span>
              </label>
            ))}
          </div>
        </div>

        <div className="pbj-actions">
          <button className="pbj-generate" onClick={run} disabled={!canRun}>
            {loading ? <><span className="spinner" /> Generating…</> : "Generate report"}
          </button>
          {rows && rows.length > 0 && (
            <button className="pbj-download" onClick={downloadCsv}>⬇ Download CSV</button>
          )}
        </div>
        {error && <div className="pbj-error">⚠ {error}</div>}
      </div>

      <div className="pbj-results">
        {loading && <div className="pbj-muted"><span className="spinner" /> Pulling shifts from Teambridge… (large ranges can take a minute)</div>}
        {!loading && rows && (
          <>
            <div className="pbj-results-head">
              <span>{meta?.count ?? rows.length} row{rows.length !== 1 ? "s" : ""} · {meta?.start} → {meta?.end}</span>
              <span className="pbj-note">* Hours w Break is an approximation (Hours Worked − 30 min) — verify/adjust the few exceptions after download.</span>
            </div>
            {rows.length === 0 ? (
              <div className="pbj-muted">No worked shifts for the selected sites and date range.</div>
            ) : (
              <div className="pbj-table-wrap">
                <table className="pbj-table">
                  <thead>
                    <tr>{COLUMNS.map(c => <th key={c[0]} className={/hours|jtc/i.test(c[0]) ? "num" : ""}>{c[1]}</th>)}</tr>
                  </thead>
                  <tbody>
                    {rows.map((r, i) => (
                      <tr key={i}>
                        {COLUMNS.map(c => <td key={c[0]} className={/hours|jtc/i.test(c[0]) ? "num" : ""}>{r[c[0]] == null ? "" : String(r[c[0]])}</td>)}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
        {!loading && !rows && !error && (
          <div className="pbj-empty">Select a date range and site(s), then <strong>Generate report</strong>.</div>
        )}
      </div>
    </div>
  );
}
